//--------------------------------------------------------------------//
//******************** deploymentInfoTab *******************************//
//--------------------------------------------------------------------//
// Displays and manages editable deployment properties: repository (via
// a repo picker, not free text — repo_url must be a gudhub-git-repo
// git_clone SSH URL) and a single multi-line Command field. The UI only
// ever has one command box — each line becomes one step in a single
// &&-chained run_command; build_command is always cleared to '' on save
// so the merged field stays the one source of truth (a deployment created
// before this change may still have both build_command/run_command set;
// its Command box shows them merged onto separate lines the first time
// it's opened). Editing either row triggers a full synchronous redeploy,
// so — unlike agent-info.tab.js's cheap metadata PATCH — saveField() here
// shows a blocking loading state rather than a quick inline spinner. Also
// surfaces status, last_error, the active toggle, and read-only metadata.
import deploymentInfoTemplateUrl from './deployment-info.template.html';
import { GUDHUB_SETTINGS } from '../../../config.js';

angular.module('deploymentsTableModule')

.directive('deploymentInfoTab', ['DeploymentsService', 'RepositoriesService', 'GhDialog', function (DeploymentsService, RepositoriesService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            deployment: '=',
            onUpdate: '&'
        },
        controller: ['$scope', 'localizationService', function ($scope, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            $scope.getStatusLabel = function (status) {
                return DeploymentsService.getStatusLabel(status);
            };
            $scope.getStatusClass = function (status) {
                return DeploymentsService.getStatusClass(status);
            };

            // The reverse-proxy URL this deployment is served at once running.
            // Slug never changes post-creation, so this is computed once.
            $scope.appUrl = GUDHUB_SETTINGS.deployment_manager_server_url + '/' + $scope.deployment.slug + '/';

            $scope.editMode = {
                repo_url: false,
                command: false
            };

            // Redeploy-triggering saves are slow (npm install etc.) — each field
            // gets its own blocking flag so the row shows a persistent loading
            // state and can't be double-submitted while a redeploy is in flight.
            $scope.saving = {
                repo_url: false,
                command: false
            };

            $scope.originalValues = {};

            // Merges the two stored backend fields into the one Command box the
            // UI shows — a deployment saved before this change may still have a
            // separate build_command, so it's displayed on its own leading line.
            function mergeCommand(deployment) {
                return [deployment.build_command, deployment.run_command].filter(Boolean).join('\n');
            }
            $scope.commandText = mergeCommand($scope.deployment);

            $scope.repoOptions = [];
            $scope.selectedRepoId = $scope.deployment.git_repo_id || null;
            $scope.loadingRepos = false;

            const safeApply = function () {
                $scope.$applyAsync();
            };

            // Deploy/Stop/Kill actions — same pattern as
            // deployments.directive.js's table-row actions (duplicated rather
            // than shared, consistent with how agent-info.tab.js and
            // agents.directive.js don't share a toggle helper either).
            $scope.deploying = false;
            $scope.stopping = false;
            $scope.killing = false;

            function runAction(deployment, flagName, apiCall) {
                if ($scope[flagName]) return;
                $scope[flagName] = true;

                apiCall()
                    .then(function (updated) {
                        Object.assign(deployment, updated);
                        $scope.onUpdate();
                    })
                    .catch(function (error) {
                        console.log('Deployment action error:', error);
                    })
                    .finally(function () {
                        $scope[flagName] = false;
                        safeApply();
                    });
            }

            $scope.deployNow = function (deployment) {
                runAction(deployment, 'deploying', () => DeploymentsService.deploy(deployment._id));
            };

            $scope.stopNow = function (deployment) {
                runAction(deployment, 'stopping', () => DeploymentsService.update({ deployment_id: deployment._id, active: 0 }));
            };

            $scope.killNow = function (deployment) {
                runAction(deployment, 'killing', () => DeploymentsService.kill(deployment._id));
            };

            // Enters edit mode for a field — for repo_url this also (re)loads the
            // repo picker options, since the list may have changed since the
            // deployment was created.
            $scope.enableEdit = async function (field) {
                if (field === 'repo_url') {
                    $scope.selectedRepoId = $scope.deployment.git_repo_id || null;
                    $scope.loadingRepos = true;
                    $scope.editMode.repo_url = true;
                    safeApply();

                    try {
                        const repos = await RepositoriesService.getList();
                        $scope.repoOptions = (Array.isArray(repos) ? repos : []).filter((repo) => !!repo.git_clone);
                    } catch (error) {
                        console.log('Failed to load repositories:', error);
                        $scope.repoOptions = [];
                    } finally {
                        $scope.loadingRepos = false;
                        safeApply();
                    }
                } else if (field === 'command') {
                    $scope.originalValues.command = $scope.commandText;
                    $scope.editMode.command = true;
                    safeApply();
                }
            };

            // Exits edit mode and restores the field to its original value.
            $scope.cancelEdit = function (field) {
                if (field === 'command') {
                    $scope.commandText = $scope.originalValues.command;
                }
                $scope.editMode[field] = false;
                safeApply();
            };

            // Saves the edited field to the server. Both repo_url and command
            // trigger a full redeploy server-side, so the loading state stays
            // up for the whole request and the row's controls are disabled.
            $scope.saveField = async function (field) {
                if (field === 'repo_url' && !$scope.selectedRepoId) {
                    return;
                }

                $scope.saving[field] = true;
                safeApply();

                try {
                    const updateData = { deployment_id: $scope.deployment._id };

                    if (field === 'repo_url') {
                        const repo = $scope.repoOptions.find((r) => r._id === $scope.selectedRepoId);
                        if (!repo) throw new Error('Selected repository is no longer available');
                        updateData.repo_url = repo.git_clone;
                    } else if (field === 'command') {
                        // Always clear build_command — the merged Command box is the
                        // single source of truth going forward, folded entirely into
                        // run_command as one && chain.
                        updateData.build_command = '';
                        updateData.run_command = DeploymentsService.joinCommandLines($scope.commandText);
                    }

                    const updated = await DeploymentsService.update(updateData);
                    Object.assign($scope.deployment, updated);

                    $scope.commandText = mergeCommand($scope.deployment);
                    $scope.originalValues.command = $scope.commandText;
                    $scope.selectedRepoId = $scope.deployment.git_repo_id;

                    $scope.editMode[field] = false;
                    $scope.onUpdate();
                } catch (error) {
                    GhDialog.show({
                        position: 'center',
                        toolbar: false,
                        template: {
                            toolbar: '',
                            resizable: false,
                            content: `
                                <div class="delete_deployment-popup">
                                    <div class="dialog-share-group">
                                        <p class="dialog-share-group__title">Error</p>
                                    </div>
                                    <div class="dialog-share-group__content">
                                        <p class="info">{{errorMessage}}</p>
                                        <div class="dialog-share-group__content__buttons">
                                            <button class="btn btn-blue" ng-click="cancel()">OK</button>
                                        </div>
                                    </div>
                                </div>
                            `
                        },
                        locals: { errorMessage: error.message },
                        controller: ['$scope', 'errorMessage', function ($scope, errorMessage) {
                            $scope.errorMessage = errorMessage;
                        }]
                    });
                } finally {
                    $scope.saving[field] = false;
                    safeApply();
                }
            };
        }],
        templateUrl: deploymentInfoTemplateUrl
    };
}]);
